import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
/** ECharts option objects built inline may use inferred `string` literals; accept loosely. */
export type ChartOption = echarts.EChartsOption | Record<string, unknown>;
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { cn } from '@/shared/lib/cn';

interface ChartCardProps {
  title: string;
  subtitle?: string;
  option: ChartOption;
  height?: number;
  className?: string;
}

export function ChartCard({ title, subtitle, option, height = 280, className }: ChartCardProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const mountChart = () => {
      const { width, height: h } = el.getBoundingClientRect();
      if (width <= 0 || h <= 0) return;

      if (!chartRef.current) {
        const instance = echarts.init(el);
        chartRef.current = instance;
        instance.setOption(option as echarts.EChartsOption, { notMerge: true });
      } else {
        chartRef.current.resize();
      }
    };

    mountChart();
    const observer = new ResizeObserver(() => mountChart());
    observer.observe(el);
    window.addEventListener('resize', mountChart);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', mountChart);
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, [option]);

  useEffect(() => {
    chartRef.current?.setOption(option, { notMerge: true });
  }, [option]);

  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
        {subtitle ? <p className="text-xs text-muted-foreground">{subtitle}</p> : null}
      </CardHeader>
      <CardContent>
        <div ref={containerRef} style={{ height }} className="w-full min-h-[12rem] min-w-0" />
      </CardContent>
    </Card>
  );
}
